"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useEventSource = exports.useEvent = exports.useCommandHandler = exports.useCommand = exports.useTimeout = exports.useTimer = exports.useDelay = exports.useForceUpdate = void 0;
const hooks_1 = require("preact/hooks");
const Command_1 = require("./Command");
function useForceUpdate() {
    const [value, setValue] = hooks_1.useState(0); // integer state
    return () => setValue(value => ++value); // update the state to force render
}
exports.useForceUpdate = useForceUpdate;
function useDelay() {
    const state = hooks_1.useMemo(() => ({ timeout: null }), [true]);
    return [(time, handler) => {
            if (state.timeout) {
                clearTimeout(state.timeout);
                state.timeout = null;
            }
            state.timeout = window.setTimeout(handler, time);
        }, () => {
            if (state.timeout) {
                clearTimeout(state.timeout);
                state.timeout = null;
            }
        }];
}
exports.useDelay = useDelay;
function useTimer(callback, time, inputs) {
    const state = hooks_1.useMemo(() => ({ interval: null }), [true]);
    hooks_1.useEffect(() => {
        if (state.interval !== null) {
            clearInterval(state.interval);
            state.interval = null;
        }
        state.interval = window.setInterval(callback, time);
        return () => {
            if (state.interval !== null) {
                clearInterval(state.interval);
                state.interval = null;
            }
        };
    }, inputs);
}
exports.useTimer = useTimer;
function useTimeout(callback, time, inputs) {
    const state = hooks_1.useMemo(() => ({ timeout: null }), [true]);
    hooks_1.useEffect(() => {
        if (state.timeout !== null) {
            clearTimeout(state.timeout);
            state.timeout = null;
        }
        const repeat = () => {
            if (state.timeout !== null) {
                state.timeout = window.setTimeout(callback, time, repeat);
            }
        };
        state.timeout = window.setTimeout(callback, time, repeat);
        return () => {
            if (state.timeout !== null) {
                clearTimeout(state.timeout);
                state.timeout = null;
            }
        };
    }, inputs);
}
exports.useTimeout = useTimeout;
function useCommand() {
    const command = hooks_1.useMemo(() => new Command_1.Command(), [true]);
    return command;
}
exports.useCommand = useCommand;
function useCommandHandler(command, handler, inputs) {
    hooks_1.useEffect(() => {
        if (!command) {
            return;
        }
        command.handler = handler;
    }, [command, ...inputs]);
}
exports.useCommandHandler = useCommandHandler;
function useEvent(event, callback) {
    hooks_1.useEffect(() => {
        event.push(callback);
        return () => {
            const index = event.indexOf(callback);
            if (index >= 0) {
                event.splice(index, 1);
            }
        };
    }, [event, callback]);
}
exports.useEvent = useEvent;
function useEventSource(url, condition, callback, inputs = []) {
    const state = hooks_1.useMemo(() => ({
        source: null,
        callback,
        reconnectCount: 0,
        hasOpened: false,
        userdata: {}
    }), [true]);
    const [reconnect, setReconnect] = hooks_1.useState(0);
    hooks_1.useEffect(() => {
        state.callback = callback;
    }, inputs);
    hooks_1.useEffect(() => {
        const connect = typeof condition === "function" ? condition() : condition;
        if (!connect && state.source !== null) {
            state.source.close();
            state.source = null;
        }
    }, [condition]);
    hooks_1.useEffect(() => {
        const connect = typeof condition === "function" ? condition() : condition;
        if (!connect) {
            return;
        }
        if (state.source !== null) {
            state.source.close();
            state.source = null;
        }
        state.source = new EventSource(url);
        state.source.onmessage = (e) => {
            if (typeof state.callback === "function") {
                state.callback(e.data);
            }
            else {
                if (state.callback.onMessage) {
                    state.callback.onMessage(e.data, state.userdata);
                }
            }
        };
        state.source.onopen = (e) => {
            state.hasOpened = true;
            state.reconnectCount = 0;
            if (typeof state.callback !== "function") {
                if (state.callback.onOpen) {
                    state.callback.onOpen(state.userdata);
                }
            }
        };
        state.source.onerror = (e) => {
            if (typeof state.callback === "function") {
                return;
            }
            const reconnectFunc = (maxAttempts) => {
                if (state.reconnectCount < maxAttempts) {
                    setReconnect(reconnect + 1);
                    state.reconnectCount++;
                }
                else if (typeof state.callback !== "function" && state.callback.onFatal) {
                    state.callback.onFatal(state.userdata);
                }
            };
            if (state.source && e.eventPhase == EventSource.CLOSED && state.hasOpened && state.callback.onClose) {
                state.source.close();
                state.source = null;
                state.callback.onClose(reconnectFunc, state.userdata);
                return;
            }
            if (state.callback.onError) {
                state.callback.onError(e, reconnectFunc, state.userdata);
            }
        };
        if (typeof state.callback === "object" && state.callback.customEvents) {
            for (const prop in state.callback.customEvents) {
                const callback = state.callback.customEvents[prop];
                state.source.addEventListener(prop, (e) => {
                    callback(e.data, state.userdata);
                });
            }
        }
        return () => {
            if (state.source !== null) {
                state.source.close();
                state.source = null;
            }
        };
    }, [url, reconnect, condition]);
}
exports.useEventSource = useEventSource;
//# sourceMappingURL=module.js.map
//# sourceMappingURL=data:application/json;base64,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